import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment,
  MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { getFeeConfig, setMonthlyFee, setUnitOverride, type MonthlyFeeConfig } from '../../lib/billing-fees-api';
import { listAdminUnits, type AdminUnit } from '../../lib/units-api';
import { formatRupiah } from '../../lib/format';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

type Override = MonthlyFeeConfig['overrides'][number];

export function FeesPage() {
  const t = useT();
  const [config, setConfig] = useState<MonthlyFeeConfig | null>(null);
  const [units, setUnits] = useState<AdminUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Monthly-fee form
  const [amountRupiah, setAmountRupiah] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [dueDay, setDueDay] = useState('10');
  const [savingFee, setSavingFee] = useState(false);

  // Override dialog
  const [overrideEditing, setOverrideEditing] = useState<Override | 'new' | null>(null);

  function applyConfig(cfg: MonthlyFeeConfig) {
    setConfig(cfg);
    setAmountRupiah(cfg.fee ? String(cfg.fee.amount_cents / 100) : '');
    setStartMonth(cfg.fee?.start_month ?? '');
    setDueDay(String(cfg.fee?.due_day ?? 10));
  }

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const [cfg, us] = await Promise.all([getFeeConfig(), listAdminUnits()]);
        applyConfig(cfg);
        setUnits(us);
      } catch (e) { setError(extractApiError(e).message); } finally { setLoading(false); }
    })();
  }, []);

  const amountNum = Number(amountRupiah);
  const dueDayNum = Number(dueDay);
  const feeValid =
    Number.isFinite(amountNum) && amountNum > 0 &&
    /^\d{4}-(0[1-9]|1[0-2])$/.test(startMonth) &&
    Number.isInteger(dueDayNum) && dueDayNum >= 1 && dueDayNum <= 28;

  async function saveFee() {
    setSavingFee(true); setError(null); setInfo(null);
    try {
      const cfg = await setMonthlyFee({
        amount_cents: Math.round(amountNum * 100),
        start_month: startMonth,
        due_day: dueDayNum,
      });
      applyConfig(cfg);
      setInfo(t('fee.saved'));
    } catch (e) { setError(extractApiError(e).message); } finally { setSavingFee(false); }
  }

  async function removeOverride(o: Override) {
    setError(null);
    try {
      applyConfig(await setUnitOverride({ unit_id: o.unit_id, override_cents: null }));
    } catch (e) { setError(extractApiError(e).message); }
  }

  const overrides = config?.overrides ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>{t('fee.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('fee.subtitle')}
        </Typography>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
      {info ? <Alert severity="success" onClose={() => setInfo(null)}>{info}</Alert> : null}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>{t('fee.monthlyFee')}</Typography>
              <Stack spacing={2.5} sx={{ maxWidth: 460 }}>
                <TextField
                  label={t('fee.amount')} value={amountRupiah}
                  onChange={(e) => setAmountRupiah(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
                  helperText={t('fee.amountHelper')}
                  fullWidth
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label={t('fee.effectiveFrom')} type="month" value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('fee.effectiveFromHelper')}
                    fullWidth
                  />
                  <TextField
                    label={t('fee.dueDay')} type="number" value={dueDay}
                    onChange={(e) => setDueDay(e.target.value)}
                    helperText={t('fee.dueDayHelper')}
                    fullWidth
                  />
                </Stack>
                <Box>
                  <Button variant="contained" onClick={saveFee} disabled={!feeValid || savingFee}>
                    {savingFee ? t('common.saving') : config?.fee ? t('fee.saveFee') : t('fee.setupFee')}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">{t('fee.overridesTitle')}</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setOverrideEditing('new')} disabled={!config?.fee}>
                  {t('fee.addOverride')}
                </Button>
              </Stack>
              {!config?.fee ? (
                <Typography variant="body2" color="text.secondary">{t('fee.setFeeFirst')}</Typography>
              ) : overrides.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('fee.noOverrides')}</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>{t('fee.colUnit')}</TableCell><TableCell>{t('fee.colPays')}</TableCell><TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {overrides.map((o) => (
                        <TableRow key={o.unit_id} hover>
                          <TableCell>{o.label}</TableCell>
                          <TableCell>
                            {o.override_cents === 0
                              ? <Chip size="small" label={t('fee.exempt')} />
                              : formatRupiah(o.override_cents)}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('common.edit')}>
                              <IconButton size="small" onClick={() => setOverrideEditing(o)}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title={t('fee.removeTooltip')}>
                              <IconButton size="small" color="error" onClick={() => removeOverride(o)}><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <OverrideDialog
        editing={overrideEditing}
        units={units}
        existing={overrides}
        onClose={() => setOverrideEditing(null)}
        onSaved={(cfg) => { applyConfig(cfg); setOverrideEditing(null); }}
      />
    </Stack>
  );
}

function OverrideDialog({ editing, units, existing, onClose, onSaved }: {
  editing: Override | 'new' | null;
  units: AdminUnit[];
  existing: Override[];
  onClose: () => void;
  onSaved: (cfg: MonthlyFeeConfig) => void;
}) {
  const t = useT();
  const isEdit = !!editing && editing !== 'new';
  const [unitId, setUnitId] = useState('');
  const [exempt, setExempt] = useState(false);
  const [amountRupiah, setAmountRupiah] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) return;
    setError(null);
    if (editing === 'new') {
      setUnitId(''); setExempt(false); setAmountRupiah('');
    } else {
      setUnitId(editing.unit_id);
      setExempt(editing.override_cents === 0);
      setAmountRupiah(editing.override_cents === 0 ? '' : String(editing.override_cents / 100));
    }
  }, [editing]);

  // When adding, only offer units that don't already have an override.
  const overriddenIds = new Set(existing.map((o) => o.unit_id));
  const pickableUnits = isEdit ? units : units.filter((u) => !overriddenIds.has(u.id));

  const amountNum = Number(amountRupiah);
  const canSave = Boolean(unitId) && (exempt || (Number.isFinite(amountNum) && amountNum > 0));

  async function save() {
    setBusy(true); setError(null);
    try {
      const cfg = await setUnitOverride({
        unit_id: unitId,
        override_cents: exempt ? 0 : Math.round(amountNum * 100),
      });
      onSaved(cfg);
    } catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={Boolean(editing)} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? t('fee.editOverride') : t('fee.addOverride')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select label={t('fee.colUnit')} value={unitId} onChange={(e) => setUnitId(e.target.value)}
            disabled={isEdit} required fullWidth
          >
            {pickableUnits.map((u) => (<MenuItem key={u.id} value={u.id}>{u.label}</MenuItem>))}
          </TextField>
          <FormControlLabel
            control={<Checkbox checked={exempt} onChange={(e) => setExempt(e.target.checked)} />}
            label={t('fee.exemptCheckbox')}
          />
          {!exempt ? (
            <TextField
              label={t('fee.amount')} value={amountRupiah} onChange={(e) => setAmountRupiah(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
              helperText={t('fee.overrideAmountHelper')}
              fullWidth
            />
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button onClick={save} variant="contained" disabled={!canSave || busy}>{busy ? t('common.saving') : t('common.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
